import React, { useState } from 'react';

import LoadPreviousButton, { LoadPreviousButtonProps } from './subcomponents/LoadPreviousButton';

import Section from 'Elements/Section';
import { ButtonProps } from 'Elements/Button';

import Grid, { Col } from 'Components/Grid';
import FiftyTwoWeeksCard, { FiftyTwoWeeksCardProps } from 'Components/Card/FiftyTwoWeeks';

import {
    Listing,
    ListItem,
    ButtonWrapper,
    LoadMoreButton,
    FiltersList,
    StyledButton,
    MiniFilterWrapper,
    StyledSelect
} from './FiftyTwoWeeks.styled';

export interface FiftyTwoWeeksCardWithCategories extends FiftyTwoWeeksCardProps {
    categories: string[];
}

export interface Props {
    id: string;
    articles: FiftyTwoWeeksCardWithCategories[];
    loadPreviousButton: LoadPreviousButtonProps;
    loadMoreButton: ButtonProps;
    categories: string[];
}

const FiftyTwoWeeks: React.FC<Props> = props => {
    const { id, articles, loadPreviousButton, loadMoreButton, categories } = props;

    const noOfArticlesToAddToList = 12;
    const previousWeekArticleIndex = articles.findIndex(
        article => article.weekStatus?.status === 'Previous week'
    );
    const initialFirstArticleIndex = previousWeekArticleIndex >= 0 ? previousWeekArticleIndex : 0;
    const initialLastArticleIndex =
        initialFirstArticleIndex <= 0 ? 12 : initialFirstArticleIndex + 11;

    const selectOptions = categories.map(category => ({
        value: category,
        label: category,
        id: `dropdown-filter-${category}`
    }));

    const [articlesList, setArticlesList] = useState(articles);
    const [activeFilter, setActiveFilter] = useState('All weeks');
    const [firstArticleToDisplayIndex, setFirstArticleToDisplayIndex] =
        useState(initialFirstArticleIndex);
    const [lastArticleToDisplayIndex, setLastArticleToDisplayIndex] =
        useState(initialLastArticleIndex);

    const displayAllArticles = () => {
        setFirstArticleToDisplayIndex(0);
        setLastArticleToDisplayIndex(articles.length);
    };

    const loadPrevious = () => {
        firstArticleToDisplayIndex - noOfArticlesToAddToList < 0
            ? setFirstArticleToDisplayIndex(0)
            : setFirstArticleToDisplayIndex(firstArticleToDisplayIndex - noOfArticlesToAddToList);
    };

    const loadMore = () => {
        setLastArticleToDisplayIndex(lastArticleToDisplayIndex + noOfArticlesToAddToList);
    };

    const filterArticles = (category: string) => {
        setActiveFilter(category);

        if (category === 'All weeks') {
            setArticlesList(articles);
            setFirstArticleToDisplayIndex(initialFirstArticleIndex);
            setLastArticleToDisplayIndex(initialLastArticleIndex);
        } else {
            setArticlesList(articles.filter(article => article.categories.includes(category)));
            displayAllArticles();
        }
    };

    return (
        <Section id={id} background>
            <Grid>
                <Col>
                    <div id="filters">
                        <Grid>
                            <Col l={[2, 12]}>
                                <FiltersList>
                                    {categories.map((category, index) => (
                                        <li key={index}>
                                            <StyledButton
                                                type="button"
                                                aria-label={`Filter by ${category}`}
                                                onClick={() => filterArticles(category)}
                                                $active={activeFilter === category}
                                            >
                                                {category}
                                            </StyledButton>
                                        </li>
                                    ))}
                                </FiltersList>
                                <MiniFilterWrapper>
                                    <StyledSelect
                                        id="filter-categories-dropdown"
                                        placeholder="All weeks"
                                        name="Filter categories dropdown"
                                        aria-labelledby="filter-by-category"
                                        defaultValue={activeFilter}
                                        handleValueChange={value =>
                                            value
                                                ? filterArticles(value)
                                                : filterArticles('All weeks')
                                        }
                                        options={selectOptions}
                                        label="Filter by:"
                                    />
                                </MiniFilterWrapper>
                            </Col>
                        </Grid>
                    </div>
                    <Listing>
                        {firstArticleToDisplayIndex > 0 && (
                            <ListItem key={0}>
                                <LoadPreviousButton
                                    {...loadPreviousButton}
                                    button={{
                                        ...loadPreviousButton.button,
                                        onClick: () => loadPrevious()
                                    }}
                                />
                            </ListItem>
                        )}
                        {articlesList
                            .slice(firstArticleToDisplayIndex, lastArticleToDisplayIndex)
                            .map((article, index) => (
                                <ListItem key={index + 1}>
                                    <FiftyTwoWeeksCard {...article} />
                                </ListItem>
                            ))}
                    </Listing>
                    {articlesList.length > lastArticleToDisplayIndex && (
                        <ButtonWrapper>
                            <LoadMoreButton {...loadMoreButton} onClick={() => loadMore()} />
                        </ButtonWrapper>
                    )}
                </Col>
            </Grid>
        </Section>
    );
};

export default FiftyTwoWeeks;
